import { expertAgentRequest } from '~/utils/expertAgentRequest'

export type ExpertAgentHomeResponse<TAgent = unknown, TModel = unknown> = {
    featured_agent?: TAgent
    agents?: TAgent[]
    model_config?: TModel
}

type ApiResponse<T> = {
    data?: T
    code?: number
    status?: number
    message?: string
    desc?: string
} | T

type LoadExpertAgentHomeOptions = {
    locale?: string | null
    token?: string | null
}

const clientRequests = new Map<string, Promise<ExpertAgentHomeResponse>>()
const clientResponses = new Map<string, ExpertAgentHomeResponse>()

function unwrap<T>(response: ApiResponse<T>): T | undefined {
    if (response && typeof response === 'object' && 'data' in response) {
        return response.data
    }
    return response as T
}

/**
 * 统一读取专家首页数据。客户端按语言和登录态复用请求与成功响应，避免 Header
 * 和专家页重复获取相同的 agents 数据。
 */
export async function loadExpertAgentHome<TAgent = unknown, TModel = unknown>(
    options: LoadExpertAgentHomeOptions = {}
): Promise<ExpertAgentHomeResponse<TAgent, TModel>> {
    const locale = options.locale?.split(/[-_]/)[0] || 'en'
    const token = options.token?.trim() || ''
    const request = async () => {
        const response = await expertAgentRequest<ApiResponse<ExpertAgentHomeResponse<TAgent, TModel>>>(
            '/api/v1/chat/home',
            { locale, token }
        )
        return unwrap(response) || {}
    }

    if (!import.meta.client) return request()

    const cacheKey = `${locale}:${token}`
    const cachedResponse = clientResponses.get(cacheKey)
    if (cachedResponse) return cachedResponse as ExpertAgentHomeResponse<TAgent, TModel>

    const existingRequest = clientRequests.get(cacheKey)
    if (existingRequest) return existingRequest as Promise<ExpertAgentHomeResponse<TAgent, TModel>>

    const pendingRequest = request()
    clientRequests.set(cacheKey, pendingRequest)
    try {
        const response = await pendingRequest
        clientResponses.set(cacheKey, response)
        return response
    } finally {
        clientRequests.delete(cacheKey)
    }
}
